"use client";

import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';

const MagneticCursor: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorDotRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const cursor = cursorRef.current;
    const cursorDot = cursorDotRef.current;
    
    if (!cursor || !cursorDot) return;

    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;

    // Mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      if (!isVisible) {
        setIsVisible(true);
      }
    };

    // Mouse enter handler for magnetic elements
    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('cursor-magnetic') || target.closest('.cursor-magnetic')) {
        setIsHovering(true);
        gsap.to(cursor, {
          scale: 1.5,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(cursorDot, {
          scale: 0.5,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    // Mouse leave handler for magnetic elements
    const handleMouseLeave = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('cursor-magnetic') || target.closest('.cursor-magnetic')) {
        setIsHovering(false);
        gsap.to(cursor, {
          scale: 1,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(cursorDot, {
          scale: 1,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    // Mouse down handler
    const handleMouseDown = () => {
      gsap.to(cursor, {
        scale: 0.8,
        duration: 0.1,
        ease: "power2.out",
      });
    };

    // Mouse up handler
    const handleMouseUp = () => {
      gsap.to(cursor, {
        scale: isHovering ? 1.5 : 1,
        duration: 0.1,
        ease: "power2.out",
      });
    };

    // Animation loop
    const animate = () => {
      // Smooth cursor movement
      cursorX += (mouseX - cursorX) * 0.1;
      cursorY += (mouseY - cursorY) * 0.1;

      gsap.set(cursor, {
        x: cursorX,
        y: cursorY,
      });

      // Dot follows with slight delay
      gsap.set(cursorDot, {
        x: cursorX,
        y: cursorY,
      });

      requestAnimationFrame(animate);
    };

    // Magnetic effect for specific elements
    const addMagneticEffect = () => {
      const magneticElements = document.querySelectorAll('.cursor-magnetic');
      
      magneticElements.forEach((element) => {
        const handleMagneticMove = (e: Event) => {
          const mouseEvent = e as MouseEvent;
          const rect = (element as HTMLElement).getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          
          const deltaX = mouseEvent.clientX - centerX;
          const deltaY = mouseEvent.clientY - centerY;
          const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          const maxDistance = Math.max(rect.width, rect.height) * 0.7;
          
          if (distance < maxDistance) {
            const strength = (maxDistance - distance) / maxDistance;
            const moveX = (deltaX * strength) * 0.3;
            const moveY = (deltaY * strength) * 0.3;
            
            gsap.to(element, {
              x: moveX,
              y: moveY,
              duration: 0.3,
              ease: "power2.out",
            });
            
            // Move cursor towards element center
            const cursorMoveX = centerX + moveX * 0.5;
            const cursorMoveY = centerY + moveY * 0.5;
            
            gsap.to(cursor, {
              x: cursorMoveX,
              y: cursorMoveY,
              duration: 0.3,
              ease: "power2.out",
            });
          }
        };
        
        const handleMagneticLeave = () => {
          gsap.to(element, {
            x: 0,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
          });
        };
        
        element.addEventListener('mousemove', handleMagneticMove);
        element.addEventListener('mouseleave', handleMagneticLeave);
        
        return () => {
          element.removeEventListener('mousemove', handleMagneticMove);
          element.removeEventListener('mouseleave', handleMagneticLeave);
        };
      });
    };

    // Event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    // Start animation loop
    animate();
    
    // Add magnetic effect
    addMagneticEffect();
    
    // Re-add magnetic effect when DOM changes
    const observer = new MutationObserver(addMagneticEffect);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      observer.disconnect();
    };
  }, [isHovering, isVisible]);

  // Hide on mobile/touch devices
  useEffect(() => {
    const checkIfMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
             ('ontouchstart' in window) || 
             (navigator.maxTouchPoints > 0);
    };

    if (checkIfMobile()) {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <>
      {/* Main cursor */}
      <div
        ref={cursorRef}
        className="fixed top-0 left-0 w-8 h-8 pointer-events-none z-[9999] mix-blend-difference"
        style={{
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="w-full h-full border border-white rounded-full opacity-50" />
      </div>
      
      {/* Cursor dot */}
      <div
        ref={cursorDotRef}
        className="fixed top-0 left-0 w-1 h-1 pointer-events-none z-[9999] bg-white rounded-full mix-blend-difference"
        style={{
          transform: 'translate(-50%, -50%)',
        }}
      />
    </>
  );
};

export default MagneticCursor;